import React from 'react';
import { CheckCircle2Icon, SearchIcon, LockIcon } from 'lucide-react';
import { FadeInSection } from './ui/FadeInSection';
export function SolutionSection() {
  const benefits = ['Self-serve signup — start in minutes', 'No demo or sales call required', 'Quick eligibility checks across payers', 'Transparent, usage-based pricing', 'Built for medical, dental, and specialty practices'];
  return <section className="py-20 bg-brand-lightBlue" data-id="element-377">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-378">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-379">
          <FadeInSection data-id="element-380">
            <div data-id="element-381">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-382">
                A Simpler Way to Verify Eligibility
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-383">
                We've streamlined the insurance verification process so your
                staff can focus on patients, not paperwork. Access real-time
                coverage data instantly through our modern, intuitive platform.
              </p>

              <ul className="space-y-4 mb-10" data-id="element-384">
                {benefits.map((benefit, index) => <li key={index} className="flex items-start" data-id="element-385">
                    <CheckCircle2Icon className="h-6 w-6 text-brand-teal mr-3 flex-shrink-0 mt-0.5" data-id="element-386" />
                    <span className="text-slate-800 font-medium" data-id="element-387">
                      {benefit}
                    </span>
                  </li>)}
              </ul>

              <a href="#how-it-works" className="inline-flex items-center text-brand-teal font-semibold hover:text-brand-dark transition-colors" data-id="element-388">
                See how it works
                <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-389">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" data-id="element-390" />
                </svg>
              </a>
            </div>
          </FadeInSection>

          <FadeInSection delay={200} data-id="element-391">
            <div className="bg-white rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.12)] border border-slate-200 overflow-hidden" data-id="element-392">
              {/* Browser Chrome — matches Hero */}
              <div className="bg-slate-100 border-b border-slate-200 px-4 py-3 flex items-center gap-3" data-id="element-393">
                <div className="flex gap-1.5" data-id="element-394">
                  <div className="w-3 h-3 rounded-full bg-slate-300" data-id="element-395"></div>
                  <div className="w-3 h-3 rounded-full bg-slate-300" data-id="element-396"></div>
                  <div className="w-3 h-3 rounded-full bg-slate-300" data-id="element-397"></div>
                </div>
                <div className="flex-1 flex justify-center" data-id="element-398">
                  <div className="bg-white border border-slate-200 rounded-md px-4 py-1.5 flex items-center gap-2 text-xs text-slate-500 max-w-[220px] w-full justify-center" data-id="element-399">
                    <LockIcon className="w-3 h-3 text-slate-400" data-id="element-400" />
                    <span data-id="element-401">enSwift — Verification Form</span>
                  </div>
                </div>
              </div>

              {/* App Content — New Verification Form */}
              <div className="p-6 md:p-8" data-id="element-402">
                <div className="mb-6" data-id="element-403">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-404">
                    New Verification
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-405">
                    Enter patient details to check coverage
                  </p>
                </div>

                <div className="space-y-5" data-id="element-406">
                  <div className="grid grid-cols-2 gap-4" data-id="element-407">
                    <div className="space-y-1.5" data-id="element-408">
                      <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-409">
                        First Name
                      </label>
                      <div className="h-10 w-full bg-slate-50 border border-slate-200 rounded-lg flex items-center px-3" data-id="element-410">
                        <span className="text-sm text-slate-400" data-id="element-411">Sarah</span>
                      </div>
                    </div>
                    <div className="space-y-1.5" data-id="element-412">
                      <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-413">
                        Last Name
                      </label>
                      <div className="h-10 w-full bg-slate-50 border border-slate-200 rounded-lg flex items-center px-3" data-id="element-414">
                        <span className="text-sm text-slate-400" data-id="element-415">Mitchell</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-416">
                    <div className="space-y-1.5" data-id="element-417">
                      <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-418">
                        Date of Birth
                      </label>
                      <div className="h-10 w-full bg-slate-50 border border-slate-200 rounded-lg flex items-center px-3" data-id="element-419">
                        <span className="text-sm text-slate-400" data-id="element-420">
                          03/15/1985
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1.5" data-id="element-421">
                      <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-422">
                        Subscriber ID
                      </label>
                      <div className="h-10 w-full bg-slate-50 border border-slate-200 rounded-lg flex items-center px-3" data-id="element-423">
                        <span className="text-sm text-slate-400" data-id="element-424">
                          XWG-882451-01
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1.5" data-id="element-425">
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-426">
                      Insurance Payer
                    </label>
                    <div className="h-10 w-full bg-slate-50 border border-slate-200 rounded-lg flex items-center px-3 justify-between" data-id="element-427">
                      <span className="text-sm text-slate-400" data-id="element-428">
                        Blue Cross Blue Shield
                      </span>
                      <SearchIcon className="h-4 w-4 text-slate-400" data-id="element-429" />
                    </div>
                  </div>

                  <div className="pt-2" data-id="element-430">
                    <button className="w-full h-12 bg-brand-teal hover:bg-brand-dark rounded-lg flex items-center justify-center text-white font-semibold shadow-sm transition-colors" data-id="element-431">
                      Check Eligibility
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </FadeInSection>
        </div>
      </div>
    </section>;
}